import React, { useCallback, useEffect, useContext, useState } from "react";
import styled from "styled-components";
import { ProductContext } from "../../context/ProductContext";
import { ResolvedProductBank, resolveProductBank } from "../../lib/productBank";

interface PaymentMethodProps {
  setSelectedPaymentMethod: (method: string | null) => void;
  productId: string;
}

const PaymentMethod: React.FC<PaymentMethodProps> = ({ setSelectedPaymentMethod, productId }) => {
  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [banks, setBanks] = useState<ResolvedProductBank[]>([]);
  const context = useContext(ProductContext);

  if (!context) {
    throw new Error("ProductContext를 찾을 수 없습니다.");
  }

  const { setBankInfo, setOrderCompleteInfo } = context;

  const applySelectedBank = useCallback((bank: ResolvedProductBank) => {
    setSelectedBank(bank.id);
    setSelectedPaymentMethod(bank.name);
    setBankInfo({
      bankName: bank.name,
      accountNumber: bank.account_number,
    });
    setOrderCompleteInfo((prev) => ({
      ...prev,
      bankName: bank.name,
      accountNumber: bank.account_number,
    }));
  }, [setBankInfo, setOrderCompleteInfo, setSelectedPaymentMethod]);

  useEffect(() => {
    let ignore = false;

    const fetchBankForProduct = async () => {
      try {
        const bank = await resolveProductBank(productId);

        if (ignore) return;

        if (!bank) {
          setBanks([]);
          setSelectedBank(null);
          setSelectedPaymentMethod(null);
          setBankInfo(null);
          return;
        }

        setBanks([bank]);
        applySelectedBank(bank);
      } catch (e) {
        if (ignore) return;
        console.error('은행 로드 중 오류:', e);
        setBanks([]);
        setSelectedBank(null);
        setSelectedPaymentMethod(null);
        setBankInfo(null);
      }
    };

    if (productId) fetchBankForProduct();
    return () => {
      ignore = true;
    };
  }, [applySelectedBank, productId, setBankInfo, setSelectedPaymentMethod]);

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    const selectedBankInfo = banks.find(bank => bank.id === selectedValue);
    if (selectedBankInfo) {
      applySelectedBank(selectedBankInfo);
    }
  };

  const selectedBankInfo = banks.find((bank) => bank.id === selectedBank) ?? null;

  return (
    <Section>
      <SectionTitle>결제수단</SectionTitle>
      <PaymentContainer>
        <ArrowIcon src="/assets/arrow-up.png" alt="Arrow Up" />
        <PaymentOptions>
          <PaymentOption>
            <JungonaraPayLogo
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/27df8873ff4179db0392ff1469b4be10171cf3a2b1f43948cb6c42de7f6f700b?placeholderIfAbsent=true&apiKey=4c6e8411f77f4625a1a5570dd0e6c4a9"
              alt="중고나라 페이 로고"
            />
            <span>무통장입금</span>
          </PaymentOption>
          <Select onChange={handleSelectChange} value={selectedBank || ""}>
            <option value="" disabled>은행을 선택하세요</option>
            {banks.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.name}
              </option>
            ))}
          </Select>
          {selectedBankInfo && (
            <PaymentInfo>
              <PaymentInfoLine>{selectedBankInfo.name}</PaymentInfoLine>
              <PaymentInfoLine>{selectedBankInfo.account_number}</PaymentInfoLine>
            </PaymentInfo>
          )}
        </PaymentOptions>
      </PaymentContainer>
    </Section>
  );
};

const Section = styled.section`
  margin-top: 39px;
`;

const SectionTitle = styled.h2`
  font-size: 45px;
  color: #000;
  font-weight: 700;
  padding: 10px;
  margin-left: 10px; /* 텍스트를 오른쪽으로 이동 */
  @media (max-width: 991px) {
    font-size: 40px;
  }
`;

const PaymentContainer = styled.div`
  position: relative; /* ArrowIcon의 위치를 제어하기 위해 사용 */
`;

const ArrowIcon = styled.img`
  position: absolute;
  top: -80px; /* 상자 바깥쪽 위로 이동 */
  right: 20px; /* 상자 바깥쪽 오른쪽으로 이동 */
  width: 30px; /* 이미지 크기 조정 */
  height: 30px;
`;

const PaymentOptions = styled.div`
  background-color: #fff;
  border-radius: 20px;
  padding: 29px;
  margin-top: 5px;
  @media (max-width: 991px) {
    padding: 20px;
  }
`;

const PaymentOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 35px;
  font-weight: 500;
  color: #000;
`;

const JungonaraPayLogo = styled.img`
  width: 70px; /* 중고나라 페이 로고 크기 */
  height: auto; /* 비율을 유지하며 크기 조정 */
`;

const Select = styled.select`
  width: 100%;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  min-height: 60px; /* 크기 줄임 */
  color: #000;
  font-size: 30px; /* 글꼴 크기 줄임 */
  font-weight: 482;
  margin-top: 25px;
  border: 1px solid rgba(0, 0, 0, 0.5);
  cursor: pointer;
  max-height: 200px;

  &:first-of-type {
    margin-top: 33px;
  }

    option {
    font-size: 18px; /* option 텍스트 크기 줄임 */
    padding: 5px; /* option 내부 여백 조정 */
  }

`;

const PaymentInfo = styled.div`
  margin-top: 34px;
  color: #000;
  font-size: 28px;
  font-weight: 654;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PaymentInfoLine = styled.span`
  line-height: 1.25;
`;

export default PaymentMethod;
